import React from 'react';
import { videoEmbedOf, fileTypeOf, FILE_MAX_BYTES, COVER_TONES, DEFAULT_COVER } from '../../learningConfig.js';
import { PizarraCourseCover, useCoverImage } from './PizarraCourseCover.jsx';

// Mi aprendizaje — small pieces shared by the coach's screens and the person's
// space: reading a file to upload it, saving a downloaded file, the video
// player and the text of a lesson. They show what the server sent; permissions
// are decided there (shared/learning.ts).

/** A file chosen in the browser → { name, data } in base64, or an error code (type or size). */
export function readFileForUpload(file) {
  return new Promise((resolve, reject) => {
    if (!fileTypeOf(file?.name)) { reject(Object.assign(new Error('file type'), { code: 'file_type' })); return; }
    if (file.size > FILE_MAX_BYTES) { reject(Object.assign(new Error('file too large'), { code: 'file_too_large' })); return; }
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, data: String(reader.result).split(',')[1] || '' });
    reader.onerror = () => reject(Object.assign(new Error('unreadable'), { code: 'unreadable' }));
    reader.readAsDataURL(file);
  });
}

/** Save what the server returned. The demo (inside a viewer that blocks downloads) only says so. */
export function saveDataUrl(dataUrl, name) {
  if (typeof window !== 'undefined' && window.__OIKE_DEMO__) return false;
  const a = document.createElement('a');
  a.href = dataUrl; a.download = name || 'archivo'; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  return true;
}

export const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** The provider's player, inside Oike, only for known providers; otherwise a link to its site. */
export function VideoPlayer({ url, title }) {
  const embed = videoEmbedOf(url);
  if (!url) return null;
  // The demo never loads a third-party player: it shows where it would play
  // (unless a preview was built to try a real video: window.__OIKE_DEMO_VIDEO__)
  if (embed && typeof window !== 'undefined' && window.__OIKE_DEMO__ && !window.__OIKE_DEMO_VIDEO__) return (
    <div style={{ display: 'grid', placeItems: 'center', aspectRatio: '16 / 9', borderRadius: 10, background: '#000', color: '#ddd', margin: '8px 0', textAlign: 'center', padding: 16, fontSize: 14 }}>
      <div>▶<br />Aquí se reproduce el video de {embed.label}, dentro de Oike.<br /><span style={{ opacity: 0.7, fontSize: 12 }}>En la demo no se carga el reproductor.</span></div>
    </div>
  );
  if (!embed) return <p style={{ margin: '8px 0' }}><a href={url} target="_blank" rel="noopener noreferrer">Abrir el video en su sitio ↗</a> <span style={{ opacity: 0.7, fontSize: 12 }}>(este proveedor no se puede reproducir dentro de Oike)</span></p>;
  // An embedded player does not reliably say when it fails (embedding disabled, blocked, offline): the provider's link is always there
  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 10, overflow: 'hidden', background: '#000' }}>
        <iframe src={embed.src} title={title || `Video de ${embed.label}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" loading="lazy" />
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 13 }}>
        <span style={{ opacity: 0.75 }}>¿No se reproduce? </span>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal, #1F7A70)', fontWeight: 600 }}>Abrir video en {embed.label} ↗</a>
      </p>
    </div>
  );
}

/** Plain text in paragraphs (no HTML is ever interpreted). */
export function LessonText({ text }) {
  if (!text) return null;
  return text.split(/\n{2,}/).map((p, i) => <p key={i} style={{ whiteSpace: 'pre-wrap', margin: '8px 0', lineHeight: 1.55 }}>{p}</p>);
}

// ── Course covers: drawn in code with the platform's area colors ──

const seedOf = (t) => [...String(t || 'curso')].reduce((n, c) => (n * 31 + c.charCodeAt(0)) >>> 0, 7);

/**
 * A course cover. With a Pizarra composition (cover.layout «A» or «B», since 04-11-2026) it is the new cover with the
 * course name and the chosen image, adapted to the place (shape: 'card' or 'header'); `loadImage(fileId)` asks the
 * server for its image with the caller's permission. Without it (courses and versions made before), the earlier
 * drawn cover, exactly as it was. `example` marks the demo's example covers.
 */
export function CourseCover({ cover, title = '', height = 140, rounded = 12, showExample = true, shape = 'card', loadImage = null }) {
  if (cover?.layout) return <PizarraCover cover={cover} title={title} rounded={rounded} shape={shape} loadImage={loadImage} />;
  return <DrawnCover cover={cover} title={title} height={height} rounded={rounded} showExample={showExample} />;
}
function PizarraCover({ cover, title, rounded, shape, loadImage }) {
  const image = useCoverImage(cover.fileId || null, loadImage);
  return <PizarraCourseCover cover={cover} title={title} shape={shape} image={image} rounded={rounded} />;
}
/** The earlier cover: a soft pattern in one tone. */
function DrawnCover({ cover, title = '', height = 140, rounded = 12, showExample = true }) {
  const c = { ...DEFAULT_COVER, ...(cover || {}) };
  const tone = COVER_TONES.find(t => t.key === c.tone) || COVER_TONES[0];
  const n = seedOf(title);
  // A small deterministic hash per index, so each title gets its own variation
  const r = (i, min, max) => { let x = (n ^ Math.imul(i + 1, 0x9E3779B1)) >>> 0; x = Math.imul(x ^ (x >>> 15), 0x85EBCA6B) >>> 0; x = (x ^ (x >>> 13)) >>> 0; return min + (x % 1000) / 1000 * (max - min); };
  const W = 320, H = 180;
  let shapes;
  if (c.style === 'circles') shapes = <>
    <circle cx={r(1, 40, 120)} cy={r(2, 40, 140)} r={r(3, 60, 90)} fill={tone.mid} opacity="0.35" />
    <circle cx={r(4, 190, 280)} cy={r(5, 60, 120)} r={r(6, 50, 80)} fill={tone.deep} opacity="0.22" />
    <circle cx={r(7, 140, 220)} cy={r(8, 120, 170)} r={r(9, 30, 55)} fill={tone.mid} opacity="0.5" />
  </>;
  else if (c.style === 'leaves') shapes = [0, 1, 2, 3, 4].map(i => (
    <ellipse key={i} cx={40 + i * 62} cy={r(i, 70, 130)} rx="22" ry="58" transform={`rotate(${r(i + 2, -40, 40)} ${40 + i * 62} ${r(i, 70, 130)})`} fill={i % 2 ? tone.deep : tone.mid} opacity={i % 2 ? 0.25 : 0.4} />
  ));
  else if (c.style === 'horizon') shapes = <>
    <circle cx={r(1, 80, 240)} cy="96" r="34" fill={tone.mid} opacity="0.55" />
    <rect x="0" y="110" width={W} height="22" fill={tone.mid} opacity="0.35" />
    <rect x="0" y="132" width={W} height="22" fill={tone.deep} opacity="0.25" />
    <rect x="0" y="154" width={W} height="26" fill={tone.deep} opacity="0.4" />
  </>;
  else if (c.style === 'dots') shapes = Array.from({ length: 40 }, (_, i) => (
    <circle key={i} cx={20 + (i % 10) * 31} cy={22 + Math.floor(i / 10) * 42} r={3 + ((n >> (i % 24)) & 7)} fill={(i + n) % 3 ? tone.mid : tone.deep} opacity={(i + n) % 3 ? 0.45 : 0.3} />
  ));
  else shapes = [0, 1, 2].map(i => (
    <path key={i} d={`M0 ${100 + i * 26} C ${80} ${r(i, 60, 120) + i * 26}, ${200} ${r(i + 3, 120, 170) + i * 22}, ${W} ${90 + i * 28} L ${W} ${H} L 0 ${H} Z`} fill={i === 2 ? tone.deep : tone.mid} opacity={[0.3, 0.35, 0.4][i]} />
  ));
  return (
    <div style={{ position: 'relative', height, borderRadius: rounded, overflow: 'hidden', background: tone.light }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" width="100%" height="100%" role="img" aria-label={`Portada de ${title || 'curso'}`} style={{ display: 'block' }}>
        <rect width={W} height={H} fill={tone.light} />
        {shapes}
      </svg>
      {c.example && showExample && <span style={{ position: 'absolute', left: 8, bottom: 8, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: 'rgba(255,255,255,0.85)', color: tone.deep }}>Portada de ejemplo</span>}
    </div>
  );
}
